import { InlineBanner } from "components/ui/inline-banner";
import { InteractiveTextLink } from "components/ui/interactive-text-link";
import { PrimaryButton } from "components/ui/primary-button";
import React from "react";
import { useFeature } from "store/selectors/hooks";
import { PlanHealth } from "./use-plan-health";

interface PlanHealthBannerProps {
    planHealth: PlanHealth;
    onViewPlan: () => void;
    noPlanMessage: string;
}

export const PlanHealthBanner: React.FC<PlanHealthBannerProps> = ({
    planHealth,
    onViewPlan,
    noPlanMessage,
}) => {
    const marketingPlaybookEnabled = useFeature("marketingPlaybook");

    if (!marketingPlaybookEnabled || !planHealth.hasRegion || planHealth.isLoading) {
        return null;
    }

    if (planHealth.hasNoTouchpoints) {
        return (
            <div className="rounded-lg bg-white p-6 text-center ring-1 ring-gray-300">
                <p className="text-sm text-gray-600">{noPlanMessage}</p>
                <div className="mt-3 flex justify-center">
                    <PrimaryButton onClick={onViewPlan}>Set up your plan</PrimaryButton>
                </div>
            </div>
        );
    }

    if (!planHealth.hasTouchpointGoal) {
        return (
            <InlineBanner type="info">
                <div className="flex items-center justify-between gap-3">
                    <p>No marketing goals have been set for you yet.</p>
                    <InteractiveTextLink
                        className="shrink-0 text-brand-600 hover:text-brand-700"
                        onClick={onViewPlan}>
                        View/edit plan
                    </InteractiveTextLink>
                </div>
            </InlineBanner>
        );
    }

    if (planHealth.isFallingShortOfGoal) {
        return (
            <InlineBanner type="warning">
                <div className="flex items-center justify-between gap-3">
                    <p>
                        Your plan is not projected to meet your touchpoint goals this
                        month.
                    </p>
                    <InteractiveTextLink
                        className="shrink-0 text-brand-600 hover:text-brand-700"
                        onClick={onViewPlan}>
                        View/edit plan
                    </InteractiveTextLink>
                </div>
            </InlineBanner>
        );
    }

    return (
        <InlineBanner type="success">
            <div className="flex items-center justify-between gap-3">
                <p>Your current plan is projected to meet your goal this month.</p>
                <InteractiveTextLink
                    className="shrink-0 text-brand-600 hover:text-brand-700"
                    onClick={onViewPlan}>
                    View/edit plan
                </InteractiveTextLink>
            </div>
        </InlineBanner>
    );
};
